import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonModal,
  IonPage,
  IonTitle,
  IonToast,
  IonToolbar,
  useIonViewWillEnter,
} from "@ionic/react";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { apiFetch } from "../api/client.js";
import DateRangeNavigator from "../components/DateRangeNavigator.jsx";
import PdfPayRunPreview from "../components/PdfPayRunPreview.jsx";
import { downloadAuthenticatedPdf } from "../utils/downloadPdf.js";
import { currentWeekRange, dateEnd, dateStart } from "../utils/date.js";

function runLabel(payRun) {
  const type = payRun.payRunBatch?.type || "Single";

  return type === "Bulk" ? "Batch run" : "Single run";
}

export default function PayrollHistoryPage() {
  const history = useHistory();
  const [filters, setFilters] = useState(currentWeekRange);
  const [payRuns, setPayRuns] = useState([]);
  const [selectedPayRun, setSelectedPayRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const load = useCallback(async (nextFilters = filters) => {
    setLoading(true);

    try {
      const params = new URLSearchParams({
        from: dateStart(nextFilters.startDate).toISOString(),
        to: dateEnd(nextFilters.endDate).toISOString(),
      });
      const payRunData = await apiFetch(`/pay-runs?${params}`);

      setPayRuns(payRunData);
      setSelectedPayRun((currentPayRun) => {
        if (!currentPayRun) {
          return null;
        }

        return payRunData.find((payRun) => payRun.id === currentPayRun.id) || currentPayRun;
      });
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useIonViewWillEnter(() => {
    load();
  });

  function updateFilters(nextFilters) {
    setFilters(nextFilters);
    load(nextFilters);
  }

  async function sendPayRun(payRunId) {
    try {
      await apiFetch(`/pay-runs/send/${payRunId}`, { method: "POST" });
      setSelectedPayRun(null);
      await load();
      setToast("Pay run sent.");
    } catch (err) {
      setToast(err.message);
    }
  }

  async function downloadPayRun(payRun) {
    if (!payRun) {
      return;
    }

    try {
      await downloadAuthenticatedPdf(`/pay-runs/pdf/${payRun.id}`, `${payRun.payRunNumber}.pdf`);
    } catch (err) {
      setToast(err.message);
    }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={() => history.push("/app/finance")}>Back</IonButton>
          </IonButtons>
          <IonTitle>Pay Run History</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <main className="page-pad">
          <DateRangeNavigator value={filters} onChange={updateFilters} />

          {!loading && (
            <section className="list-stack">
              {payRuns.map((payRun) => (
                <article className="panel row-card" key={payRun.id}>
                  <div>
                    <h3>{payRun.payRunNumber}</h3>
                    <p>
                      {runLabel(payRun)} · {new Date(payRun.createdAt).toLocaleString()}
                    </p>
                    <p>{payRun.recipientName} · {payRun.status}</p>
                    <strong>${Number(payRun.total).toFixed(2)}</strong>
                  </div>
                  <div className="row-actions">
                    <IonButton size="small" fill="outline" onClick={() => setSelectedPayRun(payRun)}>
                      Preview
                    </IonButton>
                    {payRun.status === "Draft" && (
                      <IonButton size="small" onClick={() => sendPayRun(payRun.id)}>
                        Send
                      </IonButton>
                    )}
                  </div>
                </article>
              ))}
              {payRuns.length === 0 && <div className="empty-state">No pay runs in this date range.</div>}
            </section>
          )}
        </main>

        <IonModal isOpen={Boolean(selectedPayRun)} onDidDismiss={() => setSelectedPayRun(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{selectedPayRun?.payRunNumber || "Pay Run"}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setSelectedPayRun(null)}>Close</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <main className="page-pad">
              <section className="panel form-grid">
                <p>
                  {selectedPayRun?.recipientName} · {selectedPayRun?.status}
                </p>
                <IonButton expand="block" fill="outline" onClick={() => downloadPayRun(selectedPayRun)}>
                  Download PDF
                </IonButton>
                {selectedPayRun?.status === "Draft" && (
                  <IonButton expand="block" onClick={() => sendPayRun(selectedPayRun.id)}>
                    Send This Pay Run
                  </IonButton>
                )}
              </section>
              <PdfPayRunPreview payRunId={selectedPayRun?.id} />
            </main>
          </IonContent>
        </IonModal>

        <IonToast isOpen={Boolean(toast)} message={toast} duration={2500} onDidDismiss={() => setToast("")} />
      </IonContent>
    </IonPage>
  );
}
